import React, { ReactElement } from 'react';
import { Button } from 'idsk-react-components';
import { useNavigate } from 'react-router-dom';
import './actions.scss';
import { useParams } from 'react-router';
import SledovatelAddButton from './component/SledovatelAddButton';
import SledovatelDeleteButton from './component/SledovatelDeleteButton';
import routes from '../../../../../routing/routes';
import externalRoutes from '../../../../../routing/externalRoutes';
import LegislativnyMaterialDetail from '../../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import { hasBeenInStadium } from '../../../../../function/legMaterialFunctions';
import { StadiumMaterialuEnum } from '../../../../../model/legislativnyMaterial/StadiumMaterialu';
import StadiumMaterialuHistoria from '../../../../../model/legislativnyMaterial/StadiumMaterialuHistoria';
import iconSprievodnaDokumentacia from '../../../../../../assets/images/icon-sprievodna-dokumentacia.svg';
import iconPripomienky from '../../../../../../assets/images/icon-pripomienky.svg';
import PracovneKrokyButton from '../PracovneKroky/PracovneKrokyButton';
import iconEdit from '../../../../../../assets/images/icon-edit-black.svg';
import iconDelegovat from '../../../../../../assets/images/icon-two-people-black.svg';
import iconLeft from '../../../../../../assets/images/icon-left.svg';
import { useUserContext } from '../../../../../context/UserContext';
import Secured from '../../../../Secured';
import {
  canShowNonPublicData,
  canUpdateMaterial,
  canUpdatePredkladatel,
  materialFinished,
  pouzivatelIsAdmin,
  pouzivatelIsPrihlaseny,
} from '../../../../../function/permissions';
import getSprievodnyDokumentRoute from '../../../../../function/sprievodnyDokumentRoute';
import FunkcionalitaAdministratoraButton from '../FunkcionalitaAdministratora/FunkcionalitaAdministratoraButton';
import { useSecurityContext } from '../../../../../context/SecurityContext';
import usePracovneKroky from '../PracovneKroky/hooks/usePracovneKroky';
import { currentStadiumEqualsLatest } from '../../../../../function/getLatestStadium';

interface Props {
  sledovany: boolean;
  legMaterial: LegislativnyMaterialDetail;
  historiaRecords: Array<StadiumMaterialuHistoria>;
  updateMaterial: () => void;
  publicDataVisible?: boolean;
  setPublicDataVisible?: (publicDataVisible: boolean) => void;
}

const Actions = ({
  sledovany,
  legMaterial,
  historiaRecords,
  updateMaterial,
  publicDataVisible = true,
  setPublicDataVisible,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number }>();
  const { pouzivatel } = useUserContext();
  const { accessToken } = useSecurityContext();
  const {
    pracovneKrokyRequest,
    pracovneKrokyStatus,
    reloadPracovneKroky,
  } = usePracovneKroky(type, year, index, accessToken);
  const sledovatelButton = (): ReactElement => {
    if (sledovany) {
      return (
        <SledovatelDeleteButton
          buttonText="Odstrániť proces zo sledovaných"
          legMaterialUuid={legMaterial.uuid}
          updateMaterial={updateMaterial}
        />
      );
    }
    return (
      <SledovatelAddButton materialUuid={legMaterial.uuid} updateMaterial={updateMaterial} />
    );
  };

  const pripomienkyLinkButton = (stadium: StadiumMaterialuEnum, buttonText: string, route: string): ReactElement => {
    if (hasBeenInStadium(stadium, historiaRecords)) {
      return (
        <Button
          buttonStyle="third"
          className="action__button"
          size="large"
          iconBefore={iconPripomienky}
          onClick={() => {
            window.parent.postMessage({
              type: 'iframe-to-parent-external-route',
              route,
            }, '*');
          }}
        >{buttonText}
        </Button>
      );
    }
    return null;
  };

  return (
    <div className="material-detail-actions-container">
      <div className="material-detail-actions">
        <Button
          buttonStyle="third"
          className="action__button action__sprievodna-dokumentacia"
          size="large"
          iconBefore={iconSprievodnaDokumentacia}
          onClick={() => navigate(getSprievodnyDokumentRoute(
            pouzivatel,
            legMaterial?.hasOpravnenieSprievodneDokumenty,
            currentStadiumEqualsLatest(legMaterial?.stadium?.hodnota, historiaRecords),
            true,
            historiaRecords[historiaRecords.length - 1].uuid,
            type,
            year,
            index,
          ))}
        >Sprievodná dokumentácia
        </Button>
        {pripomienkyLinkButton(
          StadiumMaterialuEnum.MPK,
          'Pripomienky',
          externalRoutes.pripomienky.list.withParams(legMaterial.cisloLegislativnehoMaterialu),
        )}
        {pripomienkyLinkButton(
          StadiumMaterialuEnum.VYJADRENIA,
          'Vyjadrenia',
          externalRoutes.vyjadrenia.list.withParams(legMaterial.cisloLegislativnehoMaterialu),
        )}
        <Secured additionalCheck={pouzivatelIsPrihlaseny(pouzivatel) && !materialFinished(legMaterial)}>
          {sledovatelButton()}
        </Secured>
        <Secured additionalCheck={legMaterial?.hasOpravneniePracovneKroky}>
          <PracovneKrokyButton
            material={legMaterial}
            reloadMaterial={updateMaterial}
            reloadPracovneKroky={reloadPracovneKroky}
            pracovneKrokyRequest={pracovneKrokyRequest}
            pracovneKrokyStatus={pracovneKrokyStatus}
            historiaRecords={historiaRecords}
          />
        </Secured>
      </div>
      <div className="material-detail-actions">
        {legMaterial && (
          <Secured additionalCheck={canUpdateMaterial(pouzivatel, legMaterial)}>
            <Button
              buttonStyle="third"
              className="action__button action__upravit-metadata-materialu"
              size="large"
              iconBefore={iconEdit}
              onClick={() => navigate(routes.procesy.edit.withParams(type, year, index))}
            >Upraviť metadáta materiálu
            </Button>
          </Secured>
        )}
        {legMaterial && (
          <Secured
            additionalCheck={canUpdatePredkladatel(pouzivatel, legMaterial)}
          >
            <Button
              buttonStyle="third"
              className="action__button"
              size="large"
              iconBefore={iconDelegovat}
              onClick={() => navigate(routes.procesy.predkladatel.edit.withParams(type, year, index))}
            >Zmeniť predkladateľa
            </Button>
          </Secured>
        )}
        {legMaterial && (
          <Secured additionalCheck={canShowNonPublicData(pouzivatel, legMaterial)}>
            <Button
              buttonStyle="third"
              className="action__button"
              size="large"
              iconBefore={iconLeft}
              onClick={() => setPublicDataVisible(!publicDataVisible)}
            >{publicDataVisible ? 'Zobraziť neverejné údaje' : 'Zobraziť verejné údaje'}
            </Button>
          </Secured>
        )}
        <Secured additionalCheck={pouzivatelIsAdmin(pouzivatel)}>
          <FunkcionalitaAdministratoraButton
            material={legMaterial}
            reloadMaterial={updateMaterial}
            reloadPracovneKroky={reloadPracovneKroky}
            pracovneKrokyRequest={pracovneKrokyRequest}
            pracovneKrokyStatus={pracovneKrokyStatus}
          />
        </Secured>
      </div>
    </div>
  );
};

export default Actions;
